import axios from 'axios';
import { API_URL } from '../constants/api';
import { universalAuthService } from './universalAuthService';
import {
  SubscriptionResponse,
  SubscriptionPlan,
  AccessInfo,
  TrialInfo,
  PaymentHistoryItem,
  CheckoutSessionResponse,
  PortalSessionResponse,
  FeatureCheckResponse,
  CreateCheckoutSessionRequest,
  CreatePortalSessionRequest,
  CancelSubscriptionRequest,
} from '../types/subscription';

// Создаем axios клиент для подписок (работает для field и referee)
const subscriptionClient = axios.create({
  baseURL: API_URL,
});

// Добавляем токен в заголовки (field или referee)
subscriptionClient.interceptors.request.use((config) => {
  // Сначала пробуем field token
  const fieldData = universalAuthService.getStoredData('field');
  if (fieldData.token) {
    config.headers.Authorization = `Bearer ${fieldData.token}`;
    return config;
  }

  // Если нет field token - пробуем referee token
  const refereeData = universalAuthService.getStoredData('referee');
  if (refereeData.token) {
    config.headers.Authorization = `Bearer ${refereeData.token}`;
  }

  return config;
});

// GET /api/subscription/my-subscription
export const getMySubscription = async (): Promise<SubscriptionResponse> => {
  const response = await subscriptionClient.get('/api/subscription/my-subscription');
  return response.data;
};

// GET /api/subscription/access-info
export const getAccessInfo = async (): Promise<AccessInfo> => {
  const response = await subscriptionClient.get('/api/subscription/access-info');
  return response.data;
};

// GET /api/subscription/trial-info
export const getTrialInfo = async (): Promise<TrialInfo> => {
  const response = await subscriptionClient.get('/api/subscription/trial-info');
  return response.data;
};

// GET /api/subscription/has-feature/{feature}
export const hasFeature = async (feature: string): Promise<FeatureCheckResponse> => {
  const response = await subscriptionClient.get(`/api/subscription/has-feature/${feature}`);
  return response.data;
};

// GET /api/subscription/plans
export const getAvailablePlans = async (): Promise<SubscriptionPlan[]> => {
  const response = await subscriptionClient.get('/api/subscription/plans');
  return response.data;
};

// POST /api/subscription/create-checkout-session
export const createCheckoutSession = async (
  request: CreateCheckoutSessionRequest
): Promise<CheckoutSessionResponse> => {
  const response = await subscriptionClient.post('/api/subscription/create-checkout-session', request);
  return response.data;
};

// POST /api/subscription/create-portal-session
export const createPortalSession = async (
  request?: CreatePortalSessionRequest
): Promise<PortalSessionResponse> => {
  const response = await subscriptionClient.post('/api/subscription/create-portal-session', request || {});
  return response.data;
};

// POST /api/subscription/cancel
export const cancelSubscription = async (
  request?: CancelSubscriptionRequest
): Promise<{ message: string }> => {
  const response = await subscriptionClient.post('/api/subscription/cancel', request || { atPeriodEnd: true });
  return response.data;
};

// GET /api/subscription/payment-history
export const getPaymentHistory = async (): Promise<PaymentHistoryItem[]> => {
  const response = await subscriptionClient.get('/api/subscription/payment-history');
  return response.data;
};

// Helper: redirect to Stripe Checkout
export const redirectToCheckout = async (planId: string, billingInterval: 'monthly' | 'yearly'): Promise<void> => {
  const response = await createCheckoutSession({ planId, billingInterval });
  if (response.url) {
    window.location.href = response.url;
  }
};

// Helper: redirect to Stripe Customer Portal
export const redirectToPortal = async (returnUrl?: string): Promise<void> => {
  const response = await createPortalSession({ returnUrl });
  if (response.url) {
    window.location.href = response.url;
  }
};
